import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Newspaper, LogOut } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

// Shared opt-in / opt-out consent dialog for the Social tab.
export default function SocialConsentDialog({ open, variant = "join", busy = false, onCancel, onConfirm }) {
  const { t } = useI18n();
  const isJoin = variant === "join";
  const Icon = isJoin ? Newspaper : LogOut;
  const title = isJoin ? t("s_join_title") : t("s_leave_title");
  const body = isJoin ? t("s_join_body") : t("s_leave_body");
  const confirmLabel = isJoin ? t("s_join_cta") : t("s_leave");

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/70 p-0 sm:p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onCancel} data-testid={`social-${variant}-dialog`}>
          <motion.div
            className="w-full max-w-md bg-[var(--rc-bg)] border border-[var(--rc-border)] rounded-t-3xl sm:rounded-3xl p-6 rc-safe-bottom"
            initial={{ y: "100%", opacity: 0.6 }} animate={{ y: 0, opacity: 1 }} exit={{ y: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}>
            <div className="mx-auto mb-5 h-1.5 w-12 rounded-full bg-[var(--rc-border)] sm:hidden" />
            <div className={`mx-auto mb-4 grid h-14 w-14 place-items-center rounded-2xl ${isJoin ? "bg-[var(--rc-gold)]/15 text-[var(--rc-gold)]" : "bg-red-500/15 text-red-400"}`}>
              <Icon size={26} />
            </div>
            <h3 className="text-center font-heading text-2xl text-[var(--rc-text)]">{title}</h3>
            <p className="mt-3 text-center text-sm text-[var(--rc-muted)] leading-relaxed">{body}</p>
            <div className="mt-6 flex flex-col gap-2.5">
              <button onClick={onConfirm} disabled={busy} data-testid={`social-${variant}-confirm`}
                className={`w-full rounded-full py-3.5 text-base font-semibold transition-colors disabled:opacity-60 ${isJoin ? "bg-[var(--rc-gold)] text-black hover:bg-[var(--rc-gold-light)]" : "bg-red-500 text-white hover:bg-red-600"}`}>
                {confirmLabel}
              </button>
              <button onClick={onCancel} disabled={busy} data-testid={`social-${variant}-cancel`}
                className="w-full rounded-full py-3.5 text-base font-medium border border-[var(--rc-border)] text-[var(--rc-text)] hover:bg-[var(--rc-surface)] transition-colors">
                {t("s_not_now")}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
